/**
 * Reusable video player factory.
 * Handles MP4/MOV, YouTube, and Vimeo — type detection, embed URL generation,
 * and element creation with autoplay/mute/loop defaults.
 *
 * Usage:
 *   import { createVideoPlayer } from './videoPlayer.js';
 *   const { el, onReady } = createVideoPlayer('https://...', container);
 *
 * For automatic setup on any element with data-video, use initDataVideoElements from
 * data-video-elements.js (skips .text-bg-video-clip — handled by textBgVideo.js).
 */

export const VIDEO_TYPE = {
  MP4: "mp4",
  MOV: "mov",
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
};

/** HTML5 `<video>` sources: MP4 and MOV (same element setup). */
export function isDirectVideoFile(type) {
  return type === VIDEO_TYPE.MP4 || type === VIDEO_TYPE.MOV;
}

export function getVideoType(url) {
  if (!url || typeof url !== "string") return null;
  const u = url.trim().toLowerCase();
  if (u.endsWith(".mp4") || u.includes(".mp4?")) return VIDEO_TYPE.MP4;
  if (u.endsWith(".mov") || u.includes(".mov?")) return VIDEO_TYPE.MOV;
  if (u.includes("youtube.com") || u.includes("youtu.be")) return VIDEO_TYPE.YOUTUBE;
  if (u.includes("vimeo.com")) return VIDEO_TYPE.VIMEO;
  return null;
}

/** YouTube watch / embed / youtu.be → video id (for IFrame API). */
export function getYoutubeVideoId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.trim().match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

/**
 * @param {string} url
 * @param {{ autoplay?: boolean, muted?: boolean, loop?: boolean, controls?: boolean, enableJsApi?: boolean }} [options]
 *   When `controls` is false, adds `modestbranding=1` and `fs=0` to reduce remaining player chrome.
 *   Use `enableJsApi` with the IFrame API (`YT.Player`).
 */
export function getYoutubeEmbedUrl(url, options = {}) {
  const {
    autoplay = true,
    muted = true,
    loop = true,
    controls = false,
    enableJsApi = false,
  } = options;
  const m = url.trim().match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (!m) return null;
  const id = m[1];
  const ap = autoplay ? 1 : 0;
  const mu = muted ? 1 : 0;
  const lp = loop ? 1 : 0;
  const ct = controls ? 1 : 0;
  const playlist = loop ? `&playlist=${id}` : "";
  const chromeless = !controls ? "&modestbranding=1&fs=0" : "";
  const jsApi =
    enableJsApi && typeof window !== "undefined" && typeof window.location !== "undefined"
      ? `&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
      : "";
  return `https://www.youtube.com/embed/${id}?autoplay=${ap}&mute=${mu}&loop=${lp}${playlist}&controls=${ct}&disablekb=${ct ? 0 : 1}&playsinline=1&showinfo=0${chromeless}${jsApi}`;
}

/**
 * @param {string} url
 * @param {{ autoplay?: boolean, muted?: boolean, loop?: boolean, background?: boolean, controls?: boolean }} [options]
 *   Set `controls: false` for a chromeless embed (no control bar on hover).
 */
export function getVimeoEmbedUrl(url, options = {}) {
  const {
    autoplay = true,
    muted = true,
    loop = true,
    background = true,
    controls,
  } = options;
  const m = url.trim().match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (!m) return null;
  const ap = autoplay ? 1 : 0;
  const bg = background ? 1 : 0;
  const chromeless =
    controls === false ? "&controls=0&title=0&byline=0&portrait=0" : "";
  return `https://player.vimeo.com/video/${m[1]}?autoplay=${ap}&muted=${muted ? 1 : 0}&loop=${loop ? 1 : 0}&autopause=0&background=${bg}${chromeless}`;
}

/**
 * Creates a <video> element configured for background autoplay.
 * @param {string} url - Direct MP4 or MOV URL.
 * @param {(video: HTMLVideoElement) => void} [onReady] - Called when video can play.
 * @param {{ autoplay?: boolean, loop?: boolean, muted?: boolean, interactive?: boolean }} [options]
 * @returns {HTMLVideoElement}
 */
export function createMp4Video(url, onReady, options = {}) {
  const { autoplay = true, loop = true, muted = true, interactive = false } = options;
  const video = document.createElement("video");
  video.muted = muted;
  video.playsInline = true;
  video.loop = loop;
  video.autoplay = autoplay;
  video.controls = false;
  video.disablePictureInPicture = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("id", "bgVid");
  video.setAttribute("disablepictureinpicture", "");
  video.preload = "metadata";
  video.style.pointerEvents = interactive ? "auto" : "none";
  video.tabIndex = interactive ? 0 : -1;

  if (typeof onReady === "function") {
    video.addEventListener("canplay", () => onReady(video), { once: true });
  }

  video.src = url;
  if (autoplay) {
    video.play().catch(() => {});
  }
  return video;
}

/**
 * Creates an <iframe> element for a YouTube or Vimeo embed URL.
 * Sets referrerpolicy so the embed request sends origin (avoids YouTube error 153 when
 * the document or ancestors use no-referrer / strict policies).
 * @param {string} embedUrl - Pre-built embed URL (use getYoutubeEmbedUrl / getVimeoEmbedUrl).
 * @param {object} [options]
 * @param {number} [options.revealDelayMs=600] - ms after load before onReady fires.
 * @param {(iframe: HTMLIFrameElement) => void} [options.onReady] - Called after load + delay.
 * @param {ReferrerPolicy} [options.referrerPolicy='strict-origin-when-cross-origin'] - Sent with the iframe fetch.
 * @param {string} [options.elementId='bgVid'] - DOM id for the iframe element.
 * @param {boolean} [options.assignSrc=true] - Set to false to assign `iframe.src` yourself after attaching listeners (avoids missing `load`).
 * @returns {HTMLIFrameElement}
 */
export function createEmbedIframe(
  embedUrl,
  {
    revealDelayMs = 600,
    onReady,
    referrerPolicy = "strict-origin-when-cross-origin",
    interactive = false,
    elementId = "bgVid",
    assignSrc = true,
  } = {}
) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("id", elementId);
  iframe.setAttribute("frameborder", "0");
  iframe.setAttribute(
    "allow",
    "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
  );
  iframe.referrerPolicy = referrerPolicy;
  iframe.loading = interactive ? "eager" : "lazy";
  iframe.style.pointerEvents = interactive ? "auto" : "none";
  iframe.tabIndex = interactive ? 0 : -1;

  if (typeof onReady === "function") {
    iframe.addEventListener(
      "load",
      () => setTimeout(() => onReady(iframe), revealDelayMs),
      { once: true }
    );
  }

  if (assignSrc) {
    iframe.src = embedUrl;
  }

  return iframe;
}

/**
 * High-level helper: detects the video type from a URL, creates the correct
 * element, appends it to `container`, and calls `onReady` when playback is
 * ready (or the embed has loaded).
 *
 * @param {string} url - Raw video URL (MP4/MOV, YouTube, or Vimeo).
 * @param {HTMLElement} container - Element to append the video/iframe into.
 * @param {object} [options]
 * @param {(el: HTMLElement) => void} [options.onReady] - Called when the media is ready.
 * @param {number} [options.revealDelayMs=600] - Delay for iframe embeds (ms).
 * @param {object} [options.youtube] - Extra options forwarded to `getYoutubeEmbedUrl`.
 * @param {object} [options.vimeo] - Extra options forwarded to `getVimeoEmbedUrl`.
 * @returns {{ el: HTMLElement|null, type: string|null }}
 *   `el` is the created <video> or <iframe>, or null if URL is unsupported.
 */
export function createVideoPlayer(
  url,
  container,
  { onReady, revealDelayMs = 600, youtube, vimeo } = {}
) {
  const type = getVideoType(url);
  if (!type) return { el: null, type: null };

  let el = null;

  if (isDirectVideoFile(type)) {
    el = createMp4Video(url, onReady);
  } else if (type === VIDEO_TYPE.YOUTUBE) {
    const embedUrl = getYoutubeEmbedUrl(url, youtube);
    if (!embedUrl) return { el: null, type };
    el = createEmbedIframe(embedUrl, { revealDelayMs, onReady });
  } else if (type === VIDEO_TYPE.VIMEO) {
    const embedUrl = getVimeoEmbedUrl(url, vimeo);
    if (!embedUrl) return { el: null, type };
    el = createEmbedIframe(embedUrl, { revealDelayMs, onReady });
  }

  if (el && container) container.appendChild(el);
  return { el, type };
}
